import {
  AnswerResponseProgress,
  type ChallengePhaseDto,
  CmmcLevel,
  CompanyType,
  type CreateChallengeDto,
  CriteriaOperator,
  CriteriaType,
  type CriterionDto,
  TechnologyType,
} from '@@types/api.types';
import {
  LONG_DESCRIPTION_MAX_LENGTH,
  LONG_DESCRIPTION_VALIDATION_MESSAGES,
  NAME_MAX_LENGTH,
  NAME_MIN_LENGTH,
  NAME_VALIDATION_MESSAGES,
} from '@azymmetric/shared/validation/validation.constants';
import { zCoercedOptional } from '@utils/zodv4.util';
import { z } from 'zod/v4';

/**
 * Custom error messages for criteria values based on type
 */
const CRITERIA_VALUE_ERROR_MESSAGES: Record<CriteriaType, string> = {
  [CriteriaType.CATEGORY]: 'Please select a category',
  [CriteriaType.CMMC_LEVEL]: 'Please select a CMMC level',
  [CriteriaType.COUNTRY]: 'Please select a country',
  [CriteriaType.DOMAINS]: 'Please select at least one domain',
  [CriteriaType.MRL_SCORE]: 'Please enter MRL score value(s)',
  [CriteriaType.ORGANIZATION_TYPE]: 'Please select an organization type',
  [CriteriaType.RESEARCH_VALIDATED]: 'Please select research validation status',
  [CriteriaType.REVENUE_RANGE]: 'Please enter revenue value(s)',
  [CriteriaType.SUBCATEGORY]: 'Please select a subcategory',
  [CriteriaType.SUBSYSTEMS]: 'Please select subsystem status',
  [CriteriaType.TRL_QUESTION]: 'Please select a TRL question and at least one answer',
  [CriteriaType.TRL_SCORE]: 'Please enter TRL score value(s)',
  [CriteriaType.TYPE]: 'Please select a technology type',
  [CriteriaType.USES]: 'Please select at least one use',
};

/**
 * Criteria types that use nested object values (min/max/value structure)
 */
const NESTED_VALUE_CRITERIA_TYPES = [CriteriaType.REVENUE_RANGE, CriteriaType.TRL_SCORE, CriteriaType.MRL_SCORE];

/**
 * Criteria schemas for each type with their specific operators and value structures
 */

// Company Criteria

const countryCriteriaSchema = z
  .object({
    operator: z
      .enum([CriteriaOperator.IS, CriteriaOperator.IS_NOT, CriteriaOperator.IS_ANY_OF, CriteriaOperator.IS_NONE_OF])
      .nullable(),
    type: z.literal(CriteriaType.COUNTRY).nullable(),
    values: z.union([z.string(), z.array(z.string())]).nullable(),
  })
  .check((ctx) => {
    const { operator, values } = ctx.value;

    if (operator === null || operator === undefined || values === null || values === undefined) {
      return;
    }

    const isSingleOperator = operator === CriteriaOperator.IS || operator === CriteriaOperator.IS_NOT;
    const isString = typeof values === 'string';
    const isArray = Array.isArray(values);

    if (isSingleOperator && !isString) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        message: 'Single value required for this operator',
        path: ['values'],
      });
    }

    if (!isSingleOperator && !isArray) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        message: 'Multiple values required for this operator',
        path: ['values'],
      });
    }

    if (isArray && values.length === 0) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        message: 'At least one country required',
        path: ['values'],
      });
    }
  });

const organizationTypeCriteriaSchema = z
  .object({
    operator: z.enum([CriteriaOperator.IS, CriteriaOperator.IS_ANY_OF, CriteriaOperator.IS_NONE_OF]).nullable(),
    type: z.literal(CriteriaType.ORGANIZATION_TYPE).nullable(),
    values: z.union([z.enum(CompanyType), z.array(z.enum(CompanyType))]).nullable(),
  })
  .check((ctx) => {
    const { operator, values } = ctx.value;

    if (operator === null || operator === undefined || values === null || values === undefined) {
      return;
    }

    const isSingleOperator = operator === CriteriaOperator.IS;
    const isString = typeof values === 'string';
    const isArray = Array.isArray(values);

    if (isSingleOperator && !isString) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        message: 'Single value required for this operator',
        path: ['values'],
      });
    }

    if (!isSingleOperator && !isArray) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        message: 'Multiple values required for this operator',
        path: ['values'],
      });
    }

    if (isArray && values.length === 0) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        message: 'At least one organization type required',
        path: ['values'],
      });
    }
  });

const revenueRangeCriteriaSchema = z.object({
  operator: z
    .enum([
      CriteriaOperator.BETWEEN,
      CriteriaOperator.LESS_THAN,
      CriteriaOperator.GREATER_THAN,
      CriteriaOperator.EQUALS,
    ])
    .nullable(),
  type: z.literal(CriteriaType.REVENUE_RANGE).nullable(),
  values: z
    .object({
      max: z.number().optional(),
      min: z.number().optional(),
      value: z.number().optional(),
    })
    .nullable(),
});

const cmmcLevelCriteriaSchema = z
  .object({
    operator: z.enum([CriteriaOperator.IS, CriteriaOperator.IS_ANY_OF, CriteriaOperator.IS_NONE_OF]).nullable(),
    type: z.literal(CriteriaType.CMMC_LEVEL).nullable(),
    values: z.union([z.enum(CmmcLevel), z.array(z.enum(CmmcLevel))]).nullable(),
  })
  .check((ctx) => {
    const { operator, values } = ctx.value;

    if (operator === null || operator === undefined || values === null || values === undefined) {
      return;
    }

    const isSingleOperator = operator === CriteriaOperator.IS;
    const isString = typeof values === 'string';
    const isArray = Array.isArray(values);

    if (isSingleOperator && !isString) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        message: 'Single value required for this operator',
        path: ['values'],
      });
    }

    if (!isSingleOperator && !isArray) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        message: 'Multiple values required for this operator',
        path: ['values'],
      });
    }

    if (isArray && values.length === 0) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        message: 'At least one CMMC level required',
        path: ['values'],
      });
    }
  });

// Technology Criteria

const categoryCriteriaSchema = z
  .object({
    operator: z.enum([CriteriaOperator.IS, CriteriaOperator.IS_ANY_OF]).nullable(),
    type: z.literal(CriteriaType.CATEGORY).nullable(),
    values: z.union([z.string().uuid(), z.array(z.uuid())]).nullable(),
  })
  .check((ctx) => {
    const { operator, values } = ctx.value;

    if (operator === null || operator === undefined || values === null || values === undefined) {
      return;
    }

    const isSingleOperator = operator === CriteriaOperator.IS;
    const isString = typeof values === 'string';
    const isArray = Array.isArray(values);

    if (isSingleOperator && !isString) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        message: 'Single value required for this operator',
        path: ['values'],
      });
    }

    if (!isSingleOperator && !isArray) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        message: 'Multiple values required for this operator',
        path: ['values'],
      });
    }

    if (isArray && values.length === 0) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        message: 'At least one category required',
        path: ['values'],
      });
    }
  });

const subcategoryCriteriaSchema = z
  .object({
    operator: z.enum([CriteriaOperator.IS, CriteriaOperator.IS_ANY_OF]).nullable(),
    type: z.literal(CriteriaType.SUBCATEGORY).nullable(),
    values: z.union([z.string().uuid(), z.array(z.uuid())]).nullable(),
  })
  .check((ctx) => {
    const { operator, values } = ctx.value;

    if (operator === null || operator === undefined || values === null || values === undefined) {
      return;
    }

    const isSingleOperator = operator === CriteriaOperator.IS;
    const isString = typeof values === 'string';
    const isArray = Array.isArray(values);

    if (isSingleOperator && !isString) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        message: 'Single value required for this operator',
        path: ['values'],
      });
    }

    if (!isSingleOperator && !isArray) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        message: 'Multiple values required for this operator',
        path: ['values'],
      });
    }

    if (isArray && values.length === 0) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        message: 'At least one subcategory required',
        path: ['values'],
      });
    }
  });

const typeCriteriaSchema = z
  .object({
    operator: z.enum([CriteriaOperator.IS, CriteriaOperator.IS_ANY_OF]).nullable(),
    type: z.literal(CriteriaType.TYPE).nullable(),
    values: z.union([z.enum(TechnologyType), z.array(z.enum(TechnologyType))]).nullable(),
  })
  .check((ctx) => {
    const { operator, values } = ctx.value;

    if (operator === null || operator === undefined || values === null || values === undefined) {
      return;
    }

    const isSingleOperator = operator === CriteriaOperator.IS;
    const isString = typeof values === 'string';
    const isArray = Array.isArray(values);

    if (isSingleOperator && !isString) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        message: 'Single value required for this operator',
        path: ['values'],
      });
    }

    if (!isSingleOperator && !isArray) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        message: 'Multiple values required for this operator',
        path: ['values'],
      });
    }

    if (isArray && values.length === 0) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        message: 'At least one technology type required',
        path: ['values'],
      });
    }
  });

const trlScoreCriteriaSchema = z.object({
  operator: z
    .enum([CriteriaOperator.RANGE, CriteriaOperator.LESS_THAN, CriteriaOperator.GREATER_THAN, CriteriaOperator.EQUALS])
    .nullable(),
  type: z.literal(CriteriaType.TRL_SCORE).nullable(),
  values: z
    .object({
      max: z.number().min(1).max(9).optional(),
      min: z.number().min(1).max(9).optional(),
      value: z.number().min(1).max(9).optional(),
    })
    .nullable(),
});

const mrlScoreCriteriaSchema = z.object({
  operator: z
    .enum([CriteriaOperator.RANGE, CriteriaOperator.LESS_THAN, CriteriaOperator.GREATER_THAN, CriteriaOperator.EQUALS])
    .nullable(),
  type: z.literal(CriteriaType.MRL_SCORE).nullable(),
  values: z
    .object({
      max: z.number().min(1).max(10).optional(),
      min: z.number().min(1).max(10).optional(),
      value: z.number().min(1).max(10).optional(),
    })
    .nullable(),
});

const trlQuestionCriteriaSchema = z.object({
  operator: z.enum([CriteriaOperator.IS_ANY_OF, CriteriaOperator.IS_NONE_OF]).nullable(),
  type: z.literal(CriteriaType.TRL_QUESTION).nullable(),
  // Inner fields stay optional so partial editing does not error; presence is enforced on submit in criteriaSchema.
  values: z
    .object({
      answers: z.array(z.enum(AnswerResponseProgress)).optional(),
      questionId: z.uuid().optional(),
    })
    .nullable(),
});

const usesCriteriaSchema = z.object({
  operator: z.enum([CriteriaOperator.INCLUDES_ANY_OF, CriteriaOperator.INCLUDES_ALL_OF]).nullable(),
  type: z.literal(CriteriaType.USES).nullable(),
  values: z.array(z.uuid()).min(1, 'At least one use required').nullable(),
});

const domainsCriteriaSchema = z.object({
  operator: z.enum([CriteriaOperator.INCLUDES_ANY_OF, CriteriaOperator.INCLUDES_ALL_OF]).nullable(),
  type: z.literal(CriteriaType.DOMAINS).nullable(),
  values: z.array(z.uuid()).min(1, 'At least one domain required').nullable(),
});

const researchValidatedCriteriaSchema = z.object({
  operator: z.literal(CriteriaOperator.IS).nullable(),
  type: z.literal(CriteriaType.RESEARCH_VALIDATED).nullable(),
  values: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((val) => (typeof val === 'string' ? val === 'true' : val))
    .nullable(),
});

const subsystemsCriteriaSchema = z.object({
  operator: z.literal(CriteriaOperator.IS).nullable(),
  type: z.literal(CriteriaType.SUBSYSTEMS).nullable(),
  values: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((val) => (typeof val === 'string' ? val === 'true' : val))
    .nullable(),
});

/**
 * Union of all criteria types
 * Type, operator, and values are nullable to allow clearing during form editing,
 * but validated for presence on submit using check()
 */
export const criteriaSchema = z
  .union([
    // Company criteria
    countryCriteriaSchema,
    organizationTypeCriteriaSchema,
    revenueRangeCriteriaSchema,
    cmmcLevelCriteriaSchema,
    // Technology criteria
    categoryCriteriaSchema,
    subcategoryCriteriaSchema,
    typeCriteriaSchema,
    trlQuestionCriteriaSchema,
    trlScoreCriteriaSchema,
    mrlScoreCriteriaSchema,
    usesCriteriaSchema,
    domainsCriteriaSchema,
    researchValidatedCriteriaSchema,
    subsystemsCriteriaSchema,
  ])
  .check((ctx) => {
    const { operator, type, values } = ctx.value;

    // Validate type is not null when submitting
    if (type === null || type === undefined) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        message: 'Criteria type is required',
        path: ['type'],
      });
      return; // Stop validation if type is missing
    }

    // Validate operator is not null when submitting
    if (operator === null || operator === undefined) {
      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        message: 'Criteria operator is required',
        path: ['operator'],
      });
      return; // Stop validation if operator is missing
    }

    const hasNestedValues = NESTED_VALUE_CRITERIA_TYPES.includes(type);

    if (hasNestedValues) {
      const nestedValues = values as { max?: number; min?: number; value?: number } | null | undefined;

      // Handle BETWEEN/RANGE operators (min/max validation)
      if (operator === CriteriaOperator.BETWEEN || operator === CriteriaOperator.RANGE) {
        const fieldLabel =
          type === CriteriaType.REVENUE_RANGE
            ? { max: 'Max revenue', min: 'Min revenue' }
            : type === CriteriaType.TRL_SCORE
              ? { max: 'Max TRL score', min: 'Min TRL score' }
              : { max: 'Max MRL score', min: 'Min MRL score' };

        if (!nestedValues?.min) {
          ctx.issues.push({
            code: 'custom',
            input: nestedValues?.min,
            message: `${fieldLabel.min} is required`,
            path: ['values', 'min'],
          });
        }
        if (!nestedValues?.max) {
          ctx.issues.push({
            code: 'custom',
            input: nestedValues?.max,
            message: `${fieldLabel.max} is required`,
            path: ['values', 'max'],
          });
        }

        // Validate min < max if both are present
        if (
          nestedValues?.min !== undefined &&
          nestedValues?.min !== null &&
          nestedValues?.max !== undefined &&
          nestedValues?.max !== null &&
          nestedValues.min >= nestedValues.max
        ) {
          ctx.issues.push({
            code: 'custom',
            input: nestedValues.min,
            message: 'Min must be less than max',
            path: ['values', 'min'],
          });
        }
      } else if (
        operator === CriteriaOperator.LESS_THAN ||
        operator === CriteriaOperator.GREATER_THAN ||
        operator === CriteriaOperator.EQUALS
      ) {
        // Handle single value operators
        const fieldLabel =
          type === CriteriaType.REVENUE_RANGE
            ? 'Revenue value'
            : type === CriteriaType.TRL_SCORE
              ? 'TRL score value'
              : 'MRL score value';

        if (nestedValues?.value === undefined || nestedValues?.value === null) {
          ctx.issues.push({
            code: 'custom',
            input: nestedValues?.value,
            message: `${fieldLabel} is required`,
            path: ['values', 'value'],
          });
        }
      }
    } else if (type === CriteriaType.TRL_QUESTION) {
      const questionValues = values as { answers?: string[]; questionId?: string } | null | undefined;

      if (!questionValues?.questionId) {
        ctx.issues.push({
          code: 'custom',
          input: questionValues,
          message: 'Please select a TRL question',
          path: ['values', 'questionId'],
        });
      }

      if (!questionValues?.answers || questionValues.answers.length === 0) {
        ctx.issues.push({
          code: 'custom',
          input: questionValues,
          message: 'Please select at least one answer',
          path: ['values', 'answers'],
        });
      }
    } else if (values === null || values === undefined) {
      const errorMessage = CRITERIA_VALUE_ERROR_MESSAGES[type] || 'Criteria values are required';

      ctx.issues.push({
        code: 'custom',
        input: ctx.value,
        message: errorMessage,
        path: ['values'],
      });
    }
  });

export type CriteriaFormData = z.infer<typeof criteriaSchema>;

/**
 * Phase schema with name and inline phase criteria
 * Position is derived from array index, not stored in form state
 */
export const phaseSchema = z.object({
  criteria: z.array(criteriaSchema),
  name: z
    .string()
    .trim()
    .min(NAME_MIN_LENGTH, { message: NAME_VALIDATION_MESSAGES.min })
    .max(NAME_MAX_LENGTH, { message: NAME_VALIDATION_MESSAGES.max }),
});

export type PhaseFormData = z.infer<typeof phaseSchema>;

/**
 * Base challenge schema for form validation
 */
const challengeBaseSchema = z.object({
  challengePhases: z.array(phaseSchema),
  description: zCoercedOptional(
    z.string().max(LONG_DESCRIPTION_MAX_LENGTH, { message: LONG_DESCRIPTION_VALIDATION_MESSAGES.max }).optional(),
  ),
  name: z
    .string()
    .trim()
    .min(NAME_MIN_LENGTH, { message: NAME_VALIDATION_MESSAGES.min })
    .max(NAME_MAX_LENGTH, { message: NAME_VALIDATION_MESSAGES.max }),
});

/**
 * Form data type inferred from base schema
 */
export type ChallengeFormData = z.infer<typeof challengeBaseSchema>;

/**
 * Step schemas for multi-step form validation
 */
export const detailsStepSchema = challengeBaseSchema.pick({
  description: true,
  name: true,
});

export const phasesStepSchema = challengeBaseSchema.pick({
  challengePhases: true,
});

/**
 * Transform criteria form data to DTO format
 * Type, operator, and values are guaranteed to be non-null at this point due to criteriaSchema validation
 */
function transformCriteriaToDto(criteria: CriteriaFormData, position: number): Omit<CriterionDto, 'id'> {
  // Convert single values to arrays for DTO compatibility
  let values = criteria.values;
  if (typeof values === 'string') {
    values = [values];
  }

  return {
    operator: criteria.operator!,
    position,
    type: criteria.type!,
    values: values as CriterionDto['values'],
  };
}

/**
 * Main challenge schema with transformation to CreateChallengeDto
 */
export const challengeSchema = challengeBaseSchema.transform((data): CreateChallengeDto => {
  // Transform phases with their criteria
  // Position is added here from array index
  const challengePhases: Omit<ChallengePhaseDto, 'id'>[] = data.challengePhases.map((phase, index) => ({
    criteria: phase.criteria.map((criterion, criterionIndex) =>
      transformCriteriaToDto(criterion, criterionIndex),
    ) as Omit<CriterionDto, 'id'>[],
    name: phase.name,
    position: index,
  }));

  return {
    challengePhases,
    description: data.description || undefined,
    name: data.name,
  };
});
